'use client';

import { cn } from '@/lib/utils';
import { LocaleKeys, Locales } from '@/utils/types';
import { enUS, es, ptBR } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useParams } from 'next/navigation';
import React from 'react';
import { CustomComponents, DayPicker as OriginalDayPicker } from 'react-day-picker';

interface ExtendedCustomComponents extends CustomComponents {
  IconLeft?: React.ComponentType;
  IconRight?: React.ComponentType;
}

const DayPicker: React.ComponentType<any> = OriginalDayPicker as React.ComponentType<any>;

interface ExtendedDayPickerProps extends React.ComponentProps<typeof DayPicker> {
  components?: Partial<ExtendedCustomComponents>;
}
export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  overlapDates?: Date[];
  isDatePicker?: boolean;
  isEventWithReservedPlace?: boolean;
};

const calendarLocales: Locales = {
  'pt-BR': ptBR,
  en: enUS,
  es: es,
};

function Calendar({
  className,
  classNames,
  overlapDates,
  showOutsideDays = true,
  isDatePicker,
  isEventWithReservedPlace,
  ...props
}: CalendarProps) {
  const { lng } = useParams() as { lng: string };

  const modifiers = {
    overlap: overlapDates || [],
  };

  return (
    <OriginalDayPicker
      showOutsideDays={showOutsideDays}
      modifiers={modifiers}
      className={cn('select-none', className)}
      classNames={
        isEventWithReservedPlace
          ? {
              chevron: 'h-4 w-4 z-10',
              button_next:
                'flex h-8 w-8 items-center [&[disabled]]:opacity-40 justify-center border border-gray2/50 hover:border-zinc-500/50 text-zinc-500',
              button_previous:
                'flex h-8 w-8 items-center [&[disabled]]:opacity-40 justify-center border border-gray2/50 hover:border-zinc-500/50 text-zinc-500',
              nav: 'flex items-center justify-between',
              caption_label: 'text-xl font-medium capitalize w-full select-none',
              month_caption: 'text-center relative -top-7',
              week: 'border-b border-gray2/50 h-20',
              month_grid: 'w-full',
              months: 'w-full',
              weekdays: 'capitalize',
              weekday: 'font-medium text-center weekdays',
              day: 'text-center align-middle [&[aria-selected]>button]:bg-primary [&[aria-selected]>button]:text-white',
              day_button: 'h-10 w-10 text-center align-middle text-foreground hover:bg-zinc-300/40',
              disabled:
                'cursor-not-allowed [&[data-disabled]>button]:line-through [&[data-disabled]>button]:text-zinc-500 [&[data-disabled]>button]:cursor-not-allowed',
              outside: '[&>button]:text-foreground',
              ...classNames,
            }
          : isDatePicker
            ? {
                chevron: 'z-10 h-4 w-4',
                button_next:
                  'flex h-8 w-8 items-center justify-center border border-gray2/50 text-zinc-500 hover:border-zinc-500/50 [&[disabled]]:opacity-40',
                button_previous:
                  'flex h-8 w-8 items-center justify-center border border-gray2/50 text-zinc-500 hover:border-zinc-500/50 [&[disabled]]:opacity-40',
                nav: 'flex items-center justify-between',
                caption_label: 'w-full select-none text-lg font-medium capitalize',
                month_caption: 'relative text-center -top-7',
                week: 'h-12',
                month_grid: 'w-full',
                months: 'w-full p-2',
                month: 'border-none',
                weekdays: 'capitalize',
                weekday: 'weekdays text-center font-medium',
                day: 'text-center align-middle [&[aria-selected]>button]:bg-primary [&[aria-selected]>button]:text-white',
                day_button: 'h-10 w-10 text-center align-middle text-foreground hover:bg-zinc-300/40',
                disabled:
                  'cursor-not-allowed [&[data-disabled]>button]:cursor-not-allowed [&[data-disabled]>button]:text-zinc-700 [&[data-disabled]>button]:line-through',
                outside: '[&>button]:text-zinc-400',
              }
            : {
                chevron: 'h-4 w-4 z-10',
                button_next:
                  'flex h-8 w-8 items-center [&[disabled]]:opacity-40 justify-center border border-gray2/50 hover:border-zinc-500/50 text-zinc-500',
                button_previous:
                  'flex h-8 w-8 items-center [&[disabled]]:opacity-40 justify-center border border-gray2/50 hover:border-zinc-500/50 text-zinc-500',
                nav: 'flex items-center justify-between',
                caption_label: 'text-[23px] max-sm:text-xl font-medium capitalize w-full select-none',
                month_caption: 'text-center relative -top-9 max-sm:-top-7',
                week: 'border-b border-gray2/50 h-20',
                month_grid: 'w-full',
                months: 'w-full',
                weekdays: 'capitalize',
                weekday: 'font-medium text-center weekdays',
                day: 'text-center align-middle [&[aria-selected]>button]:bg-primary [&[aria-selected]>button]:text-white',
                day_button: 'h-10 w-10 text-center align-middle text-foreground hover:bg-zinc-300/40',
                disabled:
                  'cursor-not-allowed [&[data-disabled]>button]:line-through [&[data-disabled]>button]:text-zinc-700 [&[data-disabled]>button]:cursor-not-allowed',
                outside: '[&>button]:text-zinc-400',
                ...classNames,
              }
      }
      components={{
        ChevronLeft: () => <ChevronLeft />,
        ChevronRight: () => <ChevronRight />,
      }}
      locale={calendarLocales[lng as LocaleKeys]}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
